import React from "react";

/**
 * HelloSir.ai brand mark — bigger, bolder, with a unique "spark + speech"
 * icon and a warm gradient that sets the brand apart from the typical
 * blue-purple SaaS look.
 *
 * Use <BrandLogo /> by default. Pass `size` to scale ("sm" | "md" | "lg" | "xl").
 */
export const BrandIcon = ({ className = "w-10 h-10" }) => (
  <span
    className={`relative inline-flex items-center justify-center rounded-[10px] shadow-[0_10px_28px_-10px_rgba(249,115,22,0.6)] overflow-hidden ${className}`}
    style={{ background: "linear-gradient(135deg,#FBBF24 0%,#F97316 50%,#EA580C 100%)" }}
    aria-hidden
  >
    {/* subtle radial highlight */}
    <span
      className="absolute inset-0 opacity-40 pointer-events-none"
      style={{ background: "radial-gradient(circle at 30% 25%, rgba(255,255,255,0.55) 0%, transparent 55%)" }}
    />
    {/* bold "H" monogram with broadcast/sound arcs — represents voice + chat + calling */}
    <svg viewBox="0 0 40 40" className="relative w-3/4 h-3/4 text-white" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      {/* H letterform */}
      <path d="M13 11 V29" strokeWidth="3.6" />
      <path d="M23 11 V29" strokeWidth="3.6" />
      <path d="M13 20 H23" strokeWidth="3.2" />
      {/* broadcast arcs on the right — sound waves emanating */}
      <path d="M29 16 Q32 20 29 24" strokeWidth="2.2" opacity="0.95" />
      <path d="M32.5 13 Q37 20 32.5 27" strokeWidth="2" opacity="0.7" />
    </svg>
    {/* spark accent */}
    <svg viewBox="0 0 32 32" className="absolute -top-1 -right-1 w-3.5 h-3.5 text-[#FEF3C7] drop-shadow-[0_2px_4px_rgba(0,0,0,0.35)]" fill="currentColor" aria-hidden>
      <path d="M16 2l2.5 7.5L26 12l-7.5 2.5L16 22l-2.5-7.5L6 12l7.5-2.5L16 2z" />
    </svg>
  </span>
);

export default function BrandLogo({ size = "md", className = "" }) {
  const dims = {
    sm: { icon: "w-8 h-8",  text: "text-base" },
    md: { icon: "w-10 h-10", text: "text-xl" },
    lg: { icon: "w-12 h-12", text: "text-2xl" },
    xl: { icon: "w-16 h-16", text: "text-3xl" },
  }[size] || { icon: "w-10 h-10", text: "text-xl" };

  return (
    <div className={`inline-flex items-center gap-2.5 ${className}`}>
      <BrandIcon className={dims.icon} />
      <span className={`font-display font-extrabold tracking-tight ${dims.text}`}>
        HelloSir
        <span style={{ background: "linear-gradient(135deg,#F97316 0%,#EA580C 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>.ai</span>
      </span>
    </div>
  );
}
